<template>
  <div>
    <VaSidebarItem
      :to="!route.children ? { name: route.name } : undefined"
      :active="isCurrentRouteActive"
      :active-color="activeColor"
      :text-color="textColorComputed"
      @click.stop="handleItemClick"
      role="button"
      hover-opacity="0.10"
    >
      <VaSidebarItemContent class="py-3 pr-2" :style="{ 'padding-left': `${padding}px` }">
        <VaIcon
          v-if="route.meta?.icon && depth === 0"
          aria-hidden="true"
          :name="route.meta.icon"
          size="20px"
          :color="iconColorComputed"
        />
        <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
          {{ t(route.displayName) }}
          <VaIcon 
            v-if="route.children" 
            :name="isOpen ? 'va-arrow-up' : 'va-arrow-down'" 
            size="20px" 
          />
        </VaSidebarItemTitle>
      </VaSidebarItemContent>
    </VaSidebarItem>

    <div v-if="route.children && isOpen" class="children-container">
      <SidebarItemRecursive
        v-for="(childRoute, childIndex) in route.children"
        :key="childIndex"
        :route="childRoute"
        :depth="depth + 1"
        :active-color="activeColor"
        :parent-open="isOpen"
        @toggle="onToggleChild"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'SidebarItemRecursive',
  props: {
    route: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    activeColor: { type: String, required: true },
    isOpen: { type: Boolean, default: false },
    parentOpen: { type: Boolean, default: true },
  },
  emits: ['toggle'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const route = useRoute()
    const router = useRouter()
    const isOpen = ref(props.isOpen)

    const padding = computed(() => 16 + (props.depth * 24))

    // Только текущий маршрут активен
    const isCurrentRouteActive = computed(() => route.name === props.route.name)

    // Проверяем, есть ли активные дети для автоматического раскрытия
    const checkActiveChildren = (r: any): boolean => {
      if (route.name === r.name) {
        isOpen.value = true
        return true
      }
      if (r.children) {
        const childActive = r.children.some((child: any) => checkActiveChildren(child))
        if (childActive) {
          isOpen.value = true
        }
        return childActive
      }
      return false
    }

    // Инициализация при создании
    checkActiveChildren(props.route)

    // Следим за изменениями маршрута
    watch(() => route.name, () => {
      checkActiveChildren(props.route)
    })

    const iconColorComputed = computed(() => isCurrentRouteActive.value ? 'links' : 'secondary')
    const textColorComputed = computed(() => isCurrentRouteActive.value ? 'links' : 'textPrimary')

    const handleItemClick = async () => {
      await router.push({ name: props.route.name })
      if (props.route.children) {
        isOpen.value = !isOpen.value
        emit('toggle', isOpen.value)
      }
    }

    const onToggleChild = () => {
      // Дополнительная логика не требуется
    }

    // Следим за состоянием родителя
    watch(() => props.parentOpen, (val) => {
      if (!val) isOpen.value = false
    })

    return {
      t,
      padding,
      isOpen,
      isCurrentRouteActive,
      iconColorComputed,
      textColorComputed,
      handleItemClick,
      onToggleChild,
    }
  }
})
</script>